import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useLang } from "@/contexts/LanguageContext";
import { useT } from "@/lib/i18n";
import { HOME } from "@/constants/testIds";
import { api } from "@/lib/api";

const Contact = ({ settings }) => {
    const { lang } = useLang();
    const t = useT(lang);
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", occasion: "" });
    const [submitting, setSubmitting] = useState(false);
    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.message.trim()) {
            toast.error(t("contact.form.required"));
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            toast.success(t("contact.form.success"));
            setForm({ name: "", email: "", phone: "", message: "", occasion: "" });
        } catch (_err) {
            toast.error(t("contact.form.error"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid={HOME.contactSection}
            className="relative py-28 md:py-40 overflow-hidden"
            style={{ background: "var(--bg-primary)" }}
        >
            <div className="mx-auto max-w-[1500px] px-6 md:px-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="md:col-span-6"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-8 h-px" style={{ background: "var(--pink)" }} />
                            <span className="font-body text-[11px] uppercase tracking-[0.32em]" style={{ color: "var(--pink)" }}>
                                {t("contact.eyebrow")}
                            </span>
                        </div>
                        <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight whitespace-pre-line" style={{ color: "var(--ink-1)" }}>
                            {t("contact.title")}
                        </h2>
                        <p className="font-body text-lg mt-8 max-w-md" style={{ color: "var(--ink-2)" }}>
                            {t("contact.desc")}
                        </p>

                        <div className="mt-10 space-y-6">
                            <a
                                href={`tel:${(settings?.phone || "(854) 252-4440").replace(/\D/g, "")}`}
                                data-testid={HOME.footerPhone}
                                className="group flex items-center gap-6 py-4 border-b"
                                style={{ borderColor: "rgba(46,16,37,0.14)" }}
                            >
                                <span className="font-body text-[11px] uppercase tracking-[0.28em] w-20 shrink-0" style={{ color: "var(--ink-mute)" }}>
                                    {t("contact.phone")}
                                </span>
                                <span className="font-serif text-3xl md:text-4xl tracking-tight" style={{ color: "var(--ink-1)" }}>
                                    {settings?.phone}
                                </span>
                                <span className="ml-auto text-xl transition-transform group-hover:translate-x-1" style={{ color: "var(--pink)" }}>→</span>
                            </a>
                            <a
                                href={`https://wa.me/1${(settings?.whatsapp || "8432510689").replace(/\D/g, "")}`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={HOME.footerWhatsapp}
                                className="group flex items-center gap-6 py-4 border-b"
                                style={{ borderColor: "rgba(46,16,37,0.14)" }}
                            >
                                <span className="font-body text-[11px] uppercase tracking-[0.28em] w-20 shrink-0" style={{ color: "var(--ink-mute)" }}>
                                    {t("contact.whatsapp")}
                                </span>
                                <span className="font-serif text-3xl md:text-4xl tracking-tight" style={{ color: "var(--ink-1)" }}>
                                    {settings?.whatsapp_display}
                                </span>
                                <span className="ml-auto text-xl transition-transform group-hover:translate-x-1" style={{ color: "var(--teal)" }}>→</span>
                            </a>
                            <div className="flex items-center gap-6 py-4">
                                <span className="font-body text-[11px] uppercase tracking-[0.28em] w-20 shrink-0" style={{ color: "var(--ink-mute)" }}>
                                    {t("contact.location")}
                                </span>
                                <span className="font-serif text-2xl md:text-3xl tracking-tight" style={{ color: "var(--ink-1)" }}>
                                    {lang === "es" ? settings?.address_es : settings?.address_en}
                                </span>
                            </div>
                        </div>
                    </motion.div>

                    <motion.form
                        onSubmit={submit}
                        data-testid={HOME.contactForm}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="md:col-span-5 md:col-start-8 p-8 md:p-10 rounded-[32px]"
                        style={{ background: "#FFF0F5", border: "1px solid rgba(232,58,116,0.2)" }}
                    >
                        <div className="grid gap-6">
                            <Field
                                testId={HOME.contactFormName}
                                label={t("contact.form.name")}
                                value={form.name}
                                onChange={(v) => set("name", v)}
                            />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <Field
                                    testId={HOME.contactFormEmail}
                                    label={t("contact.form.email")}
                                    type="email"
                                    value={form.email}
                                    onChange={(v) => set("email", v)}
                                />
                                <Field
                                    testId={HOME.contactFormPhone}
                                    label={t("contact.form.phone")}
                                    value={form.phone}
                                    onChange={(v) => set("phone", v)}
                                />
                            </div>
                            <Field
                                testId={HOME.contactFormOccasion}
                                label={t("contact.form.occasion")}
                                value={form.occasion}
                                onChange={(v) => set("occasion", v)}
                            />
                            <FieldArea
                                testId={HOME.contactFormMessage}
                                label={t("contact.form.message")}
                                value={form.message}
                                onChange={(v) => set("message", v)}
                            />
                            <button
                                type="submit"
                                data-testid={HOME.contactFormSubmit}
                                disabled={submitting}
                                className="btn-pink px-6 py-4 rounded-full text-sm font-semibold tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                                {submitting ? t("contact.form.submitting") : `${t("contact.form.submit")} →`}
                            </button>
                        </div>
                    </motion.form>
                </div>
            </div>
        </section>
    );
};

const Field = ({ label, value, onChange, type = "text", testId }) => (
    <label className="block">
        <span className="font-body text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--ink-mute)" }}>
            {label}
        </span>
        <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="mt-2 block w-full bg-transparent border-b py-2 font-serif text-xl md:text-2xl focus:outline-none focus:border-[var(--pink)] transition-colors"
            style={{ color: "var(--ink-1)", borderColor: "rgba(46,16,37,0.25)" }}
        />
    </label>
);

const FieldArea = ({ label, value, onChange, testId }) => (
    <label className="block">
        <span className="font-body text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--ink-mute)" }}>
            {label}
        </span>
        <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            data-testid={testId}
            className="mt-2 block w-full bg-transparent border-b py-2 font-serif text-xl focus:outline-none focus:border-[var(--pink)] transition-colors resize-none"
            style={{ color: "var(--ink-1)", borderColor: "rgba(46,16,37,0.25)" }}
        />
    </label>
);

export default Contact;
